import { QuickQuoteFields, QuickQuoteFormProps } from './QuickQuoteTypes';
import { filter, find, first, get, gt, isNil, lt, lte, size, tail, toNumber } from 'lodash';
import {
  DAYJS_DIFF,
  FAMILY_PLANS_ADULT,
  FAMILY_PLANS_MAX_ADULT,
  FAMILY_PLANS_MAX_AGE,
  FAMILY_PLANS_MIN
} from '@trip-ux/constants';
import { checkTrue, dayjs } from '@trip-ux/common';
import { ValidationErrors } from 'final-form';

const validateDepartureDate = (departureDate, todayDate, props, errors) => {
  const oneYearLaterDate = dayjs.utc().add(1, DAYJS_DIFF.YEARS).subtract(1, DAYJS_DIFF.DAYS);
  if (departureDate.isBefore(todayDate, DAYJS_DIFF.DAYS)) {
    errors.departureDate = props.labels.formErrors.departurePastDt;
  }
  if (departureDate.isAfter(oneYearLaterDate, DAYJS_DIFF.DAYS)) {
    errors.departureDate = props.labels.formErrors.departureFutureDt;
  }
  if (!departureDate.isValid()) {
    errors.departureDate = props.labels.formErrors.invalidDate;
  }
};

const validateReturnDate = (returnDate, departureDate, todayDate, props, errors) => {
  if (!returnDate.isValid()) {
    errors.returnDate = props.labels.formErrors.invalidDate;
  }

  // D1: Check return date is not in the past
  if (returnDate.isBefore(todayDate, DAYJS_DIFF.DAYS)) {
    errors.returnDate = props.labels.formErrors.returnPastDt;
  }

  if (returnDate.isBefore(departureDate)) {
    errors.returnDate = props.labels.formErrors.returnLtDepart;
    errors.departureDate = props.labels.formErrors.departGtReturn;
  } else if (returnDate.diff(departureDate, DAYJS_DIFF.DAYS) > props.labels.formErrors.maxTripDuration) {
    errors.returnDate = props.labels.formErrors.tripDurationGt;
    errors.departureDate = props.labels.formErrors.tripDurationGt;
  }
};

export default function (values: QuickQuoteFields, props: QuickQuoteFormProps): ValidationErrors {
  const errors: ValidationErrors = {
    ...requiredFields(values, props),
    ...validateTravellers(values, props),
    ...validateFamilyPlan(values, props)
  };

  const departureDate = dayjs(values.departureDate, props.labels.misc.dateFormat, true);
  const returnDate = dayjs(values.returnDate, props.labels.misc.dateFormat, true);
  const bookingDate = dayjs(values.bookingDate, props.labels.misc.dateFormat, true);
  const todayDate = dayjs.utc(); // D3: Use UTC to match backend dayjs.utc() comparisons

  if (values.departureDate && values.returnDate) {
    validateReturnDate(returnDate, departureDate, todayDate, props, errors);
  }

  if (values.departureDate) {
    validateDepartureDate(departureDate, todayDate, props, errors);
  }

  if (values.bookingDate) {
    if (bookingDate.isAfter(todayDate, DAYJS_DIFF.DAYS)) {
      errors.bookingDate = props.labels.formErrors.bookingFutureDt;
    }
    if (!bookingDate.isValid()) {
      errors.bookingDate = props.labels.formErrors.invalidDate;
    }
  }
  return errors;
}

const checkMembersAge = (values: QuickQuoteFields, props: QuickQuoteFormProps, errors) => {
  if (!values.travellers || (values.travellers && values.travellers.length < FAMILY_PLANS_MIN)) {
    errors.familyPlans = props.labels.formErrors.familyPlans;
  } else if (find(values.travellers, tr => gt(toNumber(tr.age), FAMILY_PLANS_MAX_AGE))) {
    errors.familyPlans = props.labels.formErrors.familyMaxAge;
  } else if (find(values.travellers, tr => gt(toNumber(tr.age), get(first(values.travellers), 'age', 0)))) {
    errors.familyPlans = props.labels.formErrors.familyPrimaryMaxAge;
  }
};

const validateFamilyPlan = (values: QuickQuoteFields, props: QuickQuoteFormProps): ValidationErrors => {
  const errors: { [prop: string]: unknown } = {};
  if (values.familyPlans && checkTrue(values.familyPlans) && find(values.travellers, tr => !isNil(tr.age))) {
    checkMembersAge(values, props, errors);
    const members = tail(values.travellers);
    const adults = size(filter(members, tr => gt(toNumber(tr.age), FAMILY_PLANS_ADULT)));
    const dependents = size(filter(members, tr => lte(toNumber(tr.age), FAMILY_PLANS_ADULT)));
    if (gt(adults, FAMILY_PLANS_MAX_ADULT) || dependents === 0 || (lte(dependents, 1) && lt(adults, 1))) {
      errors.familyPlans = props.labels.formErrors.familyRule;
    }
  }
  return errors;
};

const requiredFields = (values: QuickQuoteFields, props: QuickQuoteFormProps): ValidationErrors => {
  const errors: ValidationErrors = {};
  if (!values.bookingDate) {
    errors.bookingDate = props.labels.formErrors.required;
  }
  if (!values.departureDate) {
    errors.departureDate = props.labels.formErrors.required;
  }
  if (!values.returnDate) {
    errors.returnDate = props.labels.formErrors.required;
  }
  if (!values.province) {
    errors.province = props.labels.formErrors.required;
  }
  if (!values.travelOutsideCanada) {
    errors.travelOutsideCanada = props.labels.formErrors.required;
  }
  if (!values.multiTrip) {
    errors.multiTrip = props.labels.formErrors.required;
  }
  if (!values.familyPlans) {
    errors.familyPlans = props.labels.formErrors.required;
  }
  return errors;
};

const validateTripCost = (tripCost, labels) => {
  if (tripCost) {
    tripCost = tripCost.toString().replace(/,/g, '.');
  }
  const regF = /^\d{0,15}(\.\d{1,2})?$/;
  let tripCostError = null;
  if (toNumber(tripCost) > labels.formErrors.maxTripCost) {
    tripCostError = labels.formErrors.tripCostGt;
  } else if (toNumber(tripCost) < labels.formErrors.minTripCost) {
    tripCostError = labels.formErrors.tripCostLt;
  } else if (tripCost && !regF.test(tripCost.toString())) {
    tripCostError = labels.formErrors.tripCostInvalid;
  } else if (!tripCost) {
    tripCostError = labels.formErrors.required;
  }
  return tripCostError;
};

const validateTravellers = (values: QuickQuoteFields, props: QuickQuoteFormProps): ValidationErrors => {
  const errors: { [prop: string]: unknown } = {};
  if (!values.travellers) {
    return errors;
  }

  const travellerErrorsArray = [];
  values.travellers.forEach((traveller, index) => {
    const travellerErrors: { [prop: string]: unknown } = {};
    const age = traveller.age;
    const reg = /^\d+$/;
    if (toNumber(age) > props.labels.formErrors.maxAge) {
      travellerErrors.age = props.labels.formErrors.ageGt;
    } else if (toNumber(age) < props.labels.formErrors.minAge) {
      travellerErrors.age = props.labels.formErrors.ageLt;
    } else if (age && !reg.test(age.toString())) {
      travellerErrors.age = props.labels.formErrors.ageInvalid;
    } else if (!age) {
      travellerErrors.age = props.labels.formErrors.required;
    }

    const tripCostError = validateTripCost(traveller.tripCost, props.labels);
    if (tripCostError) {
      travellerErrors.tripCost = tripCostError;
    }

    if (Object.keys(travellerErrors).length > 0) {
      travellerErrorsArray[index] = travellerErrors;
    }
  });
  if (travellerErrorsArray.length) {
    errors.travellers = travellerErrorsArray;
  }
  return errors;
};
